import type { ManyOfManyPoll, Persisted, PollResponse } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { Typography } from '@tunasong/ui-lib'
import type { FC, ReactNode } from 'react'
import { AttendanceLine } from './attendance-line.js'
import { formatSlotFullLabel, formatSlotLabel } from './date-slot.js'
import {
  formatShortfall,
  formatWinnersLead,
  getAlsoRanIndices,
  getSlotAttendance,
  getWinnerIndices,
} from './poll-outcome.js'
import styles from './closed-summary.module.css'

export interface ClosedSummaryProps {
  poll: Persisted<ManyOfManyPoll>
  responses: Persisted<PollResponse>[]
  /** ACL-derived. The denominator in `4 of 5 available`, and what makes a missing avatar readable. */
  memberIds: string[]
  /** The responses are a query; the winning dates are not. See the render for why that matters. */
  isLoading?: boolean
  /**
   * The calendar handoff, supplied by the host because it needs the graph and the viewer's
   * identity. Absent for a respondent, who sees the same page minus this one action.
   */
  calendarAction?: ReactNode
}

/**
 * What a closed rehearsal poll looks like: *when are we playing, and who is coming?*
 *
 * This is the page a bandmate opens days later, and it replaces a single `subtitle1` line that
 * answered half of the first question and none of the second. Three rules hold it together:
 *
 * 1. **The decision leads, at heading weight.** The winning date was previously the least
 *    emphasised element on its own page, under a centred title carrying no information.
 * 2. **The numbers account for everyone.** Avatars are yes-only by design, so a member with no
 *    avatar has to be findable in the counts — otherwise "said no" and "never answered" are the
 *    same absence, which is the confusion this page was reported for.
 * 3. **Closing is terminal.** Nothing here changes state except the calendar handoff, and the
 *    page should read that way. A live-looking control on a decided page is exactly the defect
 *    the Finalize layout shipped with.
 */
export const ClosedSummary: FC<ClosedSummaryProps> = props => {
  const { poll, responses, memberIds, isLoading, calendarAction } = props

  const winners = getWinnerIndices(poll)
  const alsoRan = getAlsoRanIndices(poll, responses)
  const labels = winners.map(idx => formatSlotLabel(poll.pollOptions[idx].start ?? ''))
  const attendanceFor = (idx: number) => getSlotAttendance(responses, idx, memberIds.length)
  const yesVotersFor = (idx: number) => responses.filter(response => response.availability?.[String(idx)] === 'yes')

  /** The lead already says nothing was picked; a shortfall line below it would only say it worse. */
  const shortfall = winners.length > 0 ? formatShortfall(winners.length, poll.datesNeeded ?? 1) : undefined

  return (
    <div className={styles.container}>
      <Typography variant="h2" data-summary-block="lead" className={styles.lead}>
        {formatWinnersLead(labels)}
      </Typography>

      <ul data-summary-block="winners" className={styles.winners}>
        {winners.map(idx => {
          const slot = poll.pollOptions[idx]
          return (
            <li key={idx} data-winner className={styles.winner}>
              {/*
                One night has its date in the heading already. Repeating it here would put the
                same string on screen twice, a line apart.
              */}
              {winners.length > 1 && isDateSlot(slot) ? (
                <span className={styles.winnerLabel} title={formatSlotFullLabel(slot)}>
                  {formatSlotLabel(slot.start)}
                </span>
              ) : null}
              {/*
                Nothing rather than zero. The counts come from a query and the dates do not, so
                rendering them early says `Nobody said yes to this date.` about a night
                the whole band is coming to — on the page people open to check exactly that.
              */}
              {isLoading ? (
                <span data-attendance-skeleton className={styles.skeleton} />
              ) : (
                <AttendanceLine attendance={attendanceFor(idx)} voters={yesVotersFor(idx)} />
              )}
            </li>
          )
        })}
      </ul>

      {shortfall ? (
        <Typography variant="caption" data-summary-block="shortfall" className={styles.notice}>
          {shortfall}
        </Typography>
      ) : null}

      {/*
        Directly under the decision, and above the one remaining action. The page then reads:
        what was decided, who is coming, that it is settled, what you can still do. It sat last
        here and second on the Finalize layout, so the two surfaces told the same story in a
        different order.
      */}
      <Typography variant="caption" data-summary-block="closed" className={styles.notice}>
        This poll is closed. The dates can no longer be changed.
      </Typography>

      {calendarAction ? <div data-summary-block="calendar">{calendarAction}</div> : null}

      {!isLoading && alsoRan.length > 0 ? (
        <section data-summary-block="also-ran" className={styles.alsoRanSection}>
          <Typography variant="subtitle2" className={styles.alsoRanHeading}>
            Other dates considered
          </Typography>
          <ul className={styles.alsoRanList}>
            {alsoRan.map(idx => {
              const slot = poll.pollOptions[idx]
              if (!isDateSlot(slot)) {
                return null
              }
              return (
                <li key={idx} data-also-ran className={styles.alsoRan}>
                  <span className={styles.alsoRanLabel} title={formatSlotFullLabel(slot)}>
                    {formatSlotLabel(slot.start)}
                  </span>
                  <AttendanceLine attendance={attendanceFor(idx)} />
                </li>
              )
            })}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

export default ClosedSummary
